import type { PatientPhysicianModel } from './types';
import { CloudLightning, Phone } from 'lucide-react';
import { formatPhoneNumber } from '~/util/functions';
import { PatientPhysicianCard } from './PatientPhysicianCard';
import { PatientPhysicianFormModal } from './forms/PatientPhysicianFormModal';
import { StatusPill } from '~/shared/StatusPill';
import {
	deactivatePatientPhysicianOptions,
	deletePatientPhysicianOptions,
	getPatientPhysiciansOptions,
	PatientPhysicianAssociationEnum,
	PatientPhysicianStatusEnum,
	type DeactivatePatientPhysicianCommand
} from '@generated';
import type { StatusIntents } from '~/theme/variants';
import { useMutation } from '~/util/http/useMutation';
import { PatientPhysicianTriggersFormModal } from './forms/PatientPhysicianTriggersFormModal';
import { DataTable } from '~/shared/data-tables/DataTable';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

function RenderActionsCell(props: PatientPhysicianModel) {
	const deletePhysician = useMutation({
		...deletePatientPhysicianOptions,
		invalidateQueryKeys: [getPatientPhysiciansOptions(props.stayId).queryKey]
	});
	const deactivatePhysician = useMutation<void, DeactivatePatientPhysicianCommand>({
		...deactivatePatientPhysicianOptions,
		invalidateQueryKeys: [getPatientPhysiciansOptions(props.stayId).queryKey]
	});
	const onRemove = () => {
		if (confirm('Are you sure you want to remove this physician?')) {
			deletePhysician.mutate({ stayId: props.stayId, patientPhysicianId: props.id });
		}
	};
	const onDeactivate = () => {
		if (confirm('This physician is verified. Are you sure you want to deactivate this physician?')) {
			deactivatePhysician.mutate({ stayId: props.stayId, patientPhysicianId: props.id });
		}
	};
	const canEdit = props.patientPhysicianStatusId !== PatientPhysicianStatusEnum.RefusedByPrimary;
	const canRemove = props.patientPhysicianStatusId === PatientPhysicianStatusEnum.PendingVerification;
	return (
		<TableActions>
			<TableActions.ViewButton
				renderModal={closeModal => <PatientPhysicianFormModal stayId={props.stayId} existingPhysician={props} closeModal={closeModal} />}
			/>
			<Button
				Icon={CloudLightning}
				appearance="ghost"
				aria-label="Edit Triggers"
				useSuspense
				renderModal={closeModal => (
					<PatientPhysicianTriggersFormModal stayId={props.stayId} patientPhysicianId={props.id} closeModal={closeModal} />
				)}
			/>
			{props.patientPhysicianAssociationId !== PatientPhysicianAssociationEnum.Primary ? (
				canRemove ? (
					<TableActions.DeleteButton onClick={onRemove} isPending={deletePhysician.isPending} />
				) : (
					<TableActions.DeleteButton aria-label="Deactivate" onClick={onDeactivate} isPending={deactivatePhysician.isPending} />
				)
			) : null}
		</TableActions>
	);
}

function PhysicianStatusPill(props: PatientPhysicianModel) {
	const status: StatusIntents =
		props.patientPhysicianStatusId === PatientPhysicianStatusEnum.Verified
			? 'success'
			: props.patientPhysicianStatusId === PatientPhysicianStatusEnum.RefusedByPrimary
			? 'destructive'
			: 'warning';
	return <StatusPill intent={status}>{props.patientPhysicianStatusLabel}</StatusPill>;
}

const columns: DataTableColumnProps<PatientPhysicianModel>[] = [
	{
		key: 'name',
		header: 'Physician',
		renderCell: d => <PatientPhysicianCard {...d} />
	},
	{
		key: 'patientPhysicianStatusLabel',
		header: 'Status',
		renderCell: d => <PhysicianStatusPill {...d} />
	},
	{
		key: 'acceptingOrders',
		header: 'Orders',
		cellClassName: 'text-sm text-gray-800',
		renderCell: d => (d.acceptingOrders ? <StatusPill intent="success">Yes</StatusPill> : <StatusPill intent="neutral">No</StatusPill>)
	},
	{
		key: 'pecosCertified',
		header: 'PECOS',
		cellClassName: 'text-xs text-gray-500',
		renderCell: d => (d.physician.pecosEnrolled ? <StatusPill intent="success">Yes</StatusPill> : <StatusPill intent="neutral">No</StatusPill>)
	},
	{
		key: 'phoneNumber',
		header: 'Phone #',
		cellClassName: 'text-xs text-gray-500',
		type: 'phone',
		renderCell: d => (
			<a
				href={`tel:${d.physician.phoneNumber}`}
				className="inline-flex items-center gap-1.5 text-gray-700 transition-colors hover:text-green-700"
			>
				<Phone size={12} className="text-gray-400" />
				{formatPhoneNumber(d.physician.phoneNumber)}
			</a>
		)
	},
	{
		key: 'actions',
		header: 'Actions',
		width: 'w-24',
		renderCell: d => <RenderActionsCell {...d} />
	}
];

export function PatientPhysicianTable({ rows }: { rows: PatientPhysicianModel[] }) {
	return (
		<DataTable
			columns={columns}
			rows={rows}
			viewMore={{
				text: 'View Additional Physicians',
				threshold: 3
			}}
			getRowNotes={x => x.notes ?? null}
		/>
	);
}
